import { useTranslation } from 'react-i18next';
import { OnboardingStep, useOnboarding } from '../../../contexts/OnboardingContext';
import OnboardingTooltip from './OnboardingTooltip';
import OnboardingHighlight from './OnboardingHighlight';
import { useNavigate, useSearchParams } from 'react-router-dom';

// Map steps to their target element IDs
const STEP_TARGETS: Record<OnboardingStep, string> = {
  [OnboardingStep.WELCOME]: '',
  [OnboardingStep.OVERVIEW_CHART]: 'onboarding-overview-summary',
  [OnboardingStep.SPENDING_DISTRIBUTION]: 'onboarding-spending-chart',
  [OnboardingStep.SPENDING_TRENDS]: 'onboarding-spending-trends',
  [OnboardingStep.ASSET_OVERVIEW]: 'onboarding-asset-overview',
  [OnboardingStep.CREDIT_CARD]: 'onboarding-credit-card',
  [OnboardingStep.LOAN_BOOK]: 'onboarding-loan-book',
  [OnboardingStep.ASSET_FLUCTUATION]: 'onboarding-asset-chart',
  [OnboardingStep.ADD_TRANSACTION]: 'onboarding-add-transaction',
  [OnboardingStep.COMPLETED]: '',
};

const OnboardingOverlay = () => {
  const { t } = useTranslation(['onboarding']);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const {
    isOnboardingActive,
    currentStep,
    nextStep,
    goToStep,
    completeOnboarding,
  } = useOnboarding();

  // Get analysisType from URL params (default to 'distribution')
  const analysisType = searchParams.get('analysis') || 'distribution';

  // Don't render if onboarding is not active
  if (!isOnboardingActive || currentStep === OnboardingStep.WELCOME || currentStep === OnboardingStep.COMPLETED) {
    return null;
  }

  // Handle navigation between steps
  const handleOverviewChartNext = () => {
    nextStep();
  };

  const handleSpendingDistributionNext = () => {
    nextStep();
  };

  const handleSpendingTrendsNext = () => {
    // Navigate to assets page for next steps
    navigate('/assets');
    goToStep(OnboardingStep.ASSET_OVERVIEW);
  };

  const handleAssetOverviewNext = () => {
    // Navigate to credit card page for next step
    navigate('/assets/wallets/credit/sample_wallet_credit_techcombank');
    goToStep(OnboardingStep.CREDIT_CARD);
  };

  const handleCreditCardNext = () => {
    // Navigate to loan wallet
    goToStep(OnboardingStep.LOAN_BOOK);
    navigate('/assets/wallets/loan/sample_wallet_loan');
  };

  const handleLoanBookNext = () => {
    // Navigate to assets with fluctuations tab
    goToStep(OnboardingStep.ASSET_FLUCTUATION);
    navigate('/assets?tab=fluctuations&filter=ALL');
  };

  const handleAssetFluctuationNext = () => {
    // Navigate back to overview for final step
    navigate('/overview');
    goToStep(OnboardingStep.ADD_TRANSACTION);
  };

  const handleAddTransactionNext = () => {
    completeOnboarding();
  };

  const handleAddNow = () => {
    completeOnboarding();
    navigate('/transactions/create/basic');
  };

  // Get tooltip config for current step
  const getTooltipConfig = () => {
    switch (currentStep) {
      case OnboardingStep.OVERVIEW_CHART:
        return {
          title: t('step_overview_chart_title'),
          description: t('step_overview_chart_description'),
          position: 'bottom' as const,
          onNext: handleOverviewChartNext,
        };

      case OnboardingStep.SPENDING_DISTRIBUTION:
        return {
          title: t('step_spending_distribution_title'),
          description: t('step_spending_distribution_description'),
          position: 'top' as const,
          onNext: handleSpendingDistributionNext,
        };

      case OnboardingStep.SPENDING_TRENDS:
        return {
          title: t('step_spending_trends_title'),
          description: t('step_spending_trends_description'),
          position: 'top' as const,
          onNext: handleSpendingTrendsNext,
        };

      case OnboardingStep.ASSET_OVERVIEW:
        return {
          title: t('step_asset_overview_title'),
          description: t('step_asset_overview_description'),
          position: 'bottom' as const,
          onNext: handleAssetOverviewNext,
        };

      case OnboardingStep.CREDIT_CARD:
        return {
          title: t('step_credit_card_title'),
          description: t('step_credit_card_description'),
          position: 'top' as const,
          onNext: handleCreditCardNext,
        };

      case OnboardingStep.LOAN_BOOK:
        return {
          title: t('step_loan_book_title'),
          description: t('step_loan_book_description'),
          position: 'bottom' as const,
          onNext: handleLoanBookNext,
        };

      case OnboardingStep.ASSET_FLUCTUATION:
        return {
          title: t('step_asset_fluctuation_title'),
          description: t('step_asset_fluctuation_description'),
          position: 'bottom' as const,
          onNext: handleAssetFluctuationNext,
        };

      case OnboardingStep.ADD_TRANSACTION:
        return {
          title: t('step_add_transaction_title'),
          description: t('step_add_transaction_description'),
          position: 'top' as const,
          onNext: handleAddTransactionNext,
          showExit: true,
          showAddNow: true,
          onAddNow: handleAddNow,
        };

      default:
        return null;
    }
  };

  const tooltipConfig = getTooltipConfig();
  const targetId = STEP_TARGETS[currentStep];

  if (!tooltipConfig) return null;

  // Get highlight config based on step
  // Use borderRadius: 16 to match Paper component (borderRadius: 2 in MUI = 16px)
  const getHighlightConfig = () => {
    switch (currentStep) {
      case OnboardingStep.SPENDING_DISTRIBUTION:
      case OnboardingStep.SPENDING_TRENDS:
        // Use paddingX: 8 for distribution (Phân bổ), paddingX: -8 for trends (Xu Hướng)
        return { paddingX: analysisType === 'distribution' ? 8 : 0, paddingTop: 8, paddingBottom: 0, borderRadius: 16 };
      case OnboardingStep.CREDIT_CARD:
        return { padding: 8, borderRadius: 16 };
      case OnboardingStep.LOAN_BOOK:
        return { padding: 0, borderRadius: 16 };
      case OnboardingStep.ADD_TRANSACTION:
        // FAB is a detached floating circle — use a circular spotlight around it
        return { padding: 6, borderRadius: 999 };
      default:
        return { padding: 0, borderRadius: 16 };
    }
  };

  const highlightConfig = getHighlightConfig();

  return (
    <>
      {/* Highlight with spotlight effect */}
      {targetId && (
        <OnboardingHighlight
          targetId={targetId}
          {...highlightConfig}
        />
      )}

      {/* Tooltip */}
      <OnboardingTooltip
        step={currentStep}
        targetId={targetId}
        title={tooltipConfig.title}
        description={tooltipConfig.description}
        position={tooltipConfig.position}
        onNext={tooltipConfig.onNext}
        showExit={(tooltipConfig as any).showExit}
        showAddNow={(tooltipConfig as any).showAddNow}
        onAddNow={(tooltipConfig as any).onAddNow}
      />
    </>
  );
};

export default OnboardingOverlay;
